import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ChevronRight, ListChecks } from 'lucide-react';
import { onboardingAPI } from '../../services/api';
import { ONBOARDING_STEPS, onboardingHref } from './onboardingSteps';

/**
 * Persistent onboarding ribbon (plan §9 "interlinked journey").
 *
 * Rides on top of each step's REAL configuration page (rendered by
 * DashboardLayout when the URL carries ?onboarding=1) so the steps feel like
 * one connected flow instead of isolated pages: it shows progress, marks the
 * current step, and offers a single "Next step" action that carries the
 * onboarding context forward. No duplicate onboarding-only forms.
 */
export default function OnboardingRibbon({ onNext } = {}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);

  const currentId = params.get('step') || ONBOARDING_STEPS[0].id;
  const currentIndex = Math.max(0, ONBOARDING_STEPS.findIndex((s) => s.id === currentId));

  useEffect(() => {
    let cancelled = false;
    onboardingAPI.getStatus()
      .then((res) => { if (!cancelled && res?.success) setStatus(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const steps = status?.steps || {};
  const passwordDone = Boolean(steps.set_password);

  // Block later steps until password is set (issue #20 step 0).
  const next = !passwordDone
    ? (currentId === 'set_password' ? null : ONBOARDING_STEPS[0])
    : ONBOARDING_STEPS.slice(currentIndex + 1).find((s) => !steps[s.id])
      || ONBOARDING_STEPS[currentIndex + 1]
      || null;

  return (
    <div className="mb-4 rounded-xl border border-ga-primary/20 bg-ga-primary/5 p-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium text-ga-text-primary">
          <ListChecks className="h-4 w-4 text-ga-primary" />
          <span>Setup · step {currentIndex + 1} of {ONBOARDING_STEPS.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="text-xs text-ga-text-muted hover:text-ga-text-primary"
          >
            Back to setup
          </button>
          {next && (
            <button
              type="button"
              onClick={() => onNext ? onNext(next) : navigate(onboardingHref(next))}
              className="inline-flex items-center gap-1 rounded-lg bg-ga-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-ga-primary/90"
            >
              {!passwordDone && currentId !== 'set_password'
                ? <>Set password first <ChevronRight className="h-4 w-4" /></>
                : <>Next: {next.short} <ChevronRight className="h-4 w-4" /></>}
            </button>
          )}
        </div>
      </div>

      {/* Compact stepper */}
      <ol className="mt-3 flex items-center gap-1 overflow-x-auto">
        {ONBOARDING_STEPS.map((s, i) => {
          const done = Boolean(steps[s.id]);
          const isCurrent = i === currentIndex;
          return (
            <li key={s.id} className="flex items-center shrink-0">
              <button
                type="button"
                disabled={!passwordDone && s.id !== 'set_password' && !done}
                onClick={() => {
                  if (!passwordDone && s.id !== 'set_password') return;
                  navigate(onboardingHref(s));
                }}
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors ${
                  isCurrent
                    ? 'bg-ga-primary text-white'
                    : done
                      ? 'text-ga-primary hover:bg-ga-primary/10'
                      : 'text-ga-text-muted hover:bg-ga-bg-page'
                }`}
              >
                <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${
                  done ? 'bg-ga-primary text-white' : isCurrent ? 'bg-white text-ga-primary' : 'bg-ga-border text-ga-text-muted'
                }`}>
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                {s.short}
              </button>
              {i < ONBOARDING_STEPS.length - 1 && (
                <ChevronRight className="h-3 w-3 text-ga-text-muted/50" />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
